import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { setWorkspaceData } from "@store/slices/workspaceSlice";
import { selectSelectedOrgId, setSelectedOrgId } from "@store/slices/viewSlice";
import { selectCurrentUserOrgs } from "@store/slices/orgsSlice";
import { saveAllDirtyResources } from "@store/thunks/workspaceThunks";
import { useNotification } from "@shared/contexts/NotificationContext";
import { createWorkspace, getWorkspaces } from "@services/api";
import type { RootState } from "@store/index";
import type { Workspace } from "@/types/models";

const WorkspaceSwitcher = () => {
  const dispatch = useAppDispatch();
  const currentWorkspaceId = useAppSelector(
    (state: RootState) => state.workspace.workspace_id
  );
  const userId = useAppSelector((state: RootState) => state.user.userId);
  const selectedOrgId = useAppSelector(selectSelectedOrgId);
  const userOrgs = useAppSelector(selectCurrentUserOrgs);

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchWorkspaces = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const result = await getWorkspaces();
    setLoading(false);
    if (!result.success) {
      console.error("WorkspaceSwitcher: failed to fetch workspaces", result.error);
      return;
    }
    setWorkspaces(Array.isArray(result.data) ? result.data : []);
  }, [userId]);

  useEffect(() => {
    void fetchWorkspaces();
  }, [fetchWorkspaces]);

  // Match WorkspaceDropdown's context filter so the switcher stays in
  // sync with whichever org/personal context the user picked in /annogram.
  const contextWorkspaces = workspaces.filter((w) =>
    selectedOrgId === null ? !w.org_id : w.org_id === selectedOrgId
  );
  const activeOrg = userOrgs.find((o) => o.org_id === selectedOrgId);
  const contextLabel = activeOrg ? activeOrg.name : "Personal";

  const handleChange = async (e: SelectChangeEvent<string>) => {
    const nextId = e.target.value;
    const next = contextWorkspaces.find((w) => w.workspace_id === nextId);
    if (!next || next.workspace_id === currentWorkspaceId) return;

    // Mirror WorkspaceDropdown: flush any dirty resource state before
    // swapping the workspace context so unsaved work isn't lost.
    try {
      await dispatch(saveAllDirtyResources()).unwrap();
    } catch (err) {
      console.error("WorkspaceSwitcher: save before switch failed", err);
    }

    dispatch(
      setWorkspaceData({
        workspace_id: next.workspace_id,
        workspace_name: next.workspace_name,
        shared_data: next.shared_data,
        user_data: next.user_data,
        workspace_permissions: next.workspace_permissions,
      })
    );
  };

  const hasCurrentInContext = contextWorkspaces.some(
    (w) => w.workspace_id === currentWorkspaceId
  );

  // null = Personal context. Stored as the string "personal" in the Select
  // because MUI's Select treats null/undefined as "no value selected" and
  // would render the field as empty.
  const PERSONAL = "personal";
  const contextValue = selectedOrgId === null ? PERSONAL : selectedOrgId;
  const handleContextChange = (e: SelectChangeEvent<string>) => {
    const next = e.target.value === PERSONAL ? null : e.target.value;
    dispatch(setSelectedOrgId(next));
  };

  // Create a new workspace in the current context (org or Personal).
  const { showSuccess } = useNotification();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const openCreate = () => {
    setNewName("");
    setCreateError("");
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      setCreateError("Workspace name is required.");
      return;
    }
    setCreating(true);
    setCreateError("");
    // No categories → backend applies its defaults. orgId scopes it to
    // the active context so it shows up in the switcher immediately.
    const result = await createWorkspace(name, undefined, selectedOrgId);
    if (!result.success) {
      setCreating(false);
      setCreateError(result.error.message || "Could not create workspace.");
      return;
    }
    // Flush unsaved work before switching context to the new workspace.
    try {
      await dispatch(saveAllDirtyResources()).unwrap();
    } catch (err) {
      console.error("WorkspaceSwitcher: save before switch failed", err);
    }
    const w = result.data;
    dispatch(
      setWorkspaceData({
        workspace_id: w.workspace_id,
        workspace_name: w.workspace_name,
        shared_data: w.shared_data,
        user_data: w.user_data,
        workspace_permissions: w.workspace_permissions,
      })
    );
    await fetchWorkspaces();
    setCreating(false);
    setCreateOpen(false);
    showSuccess(`Created “${name}”.`);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 4,
        pt: 3,
        pb: 1,
        flexWrap: "wrap",
      }}
    >
      {userOrgs.length > 0 && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="context-switcher-label">Context</InputLabel>
          <Select
            labelId="context-switcher-label"
            label="Context"
            value={contextValue}
            onChange={handleContextChange}
          >
            <MenuItem value={PERSONAL}>Personal</MenuItem>
            {userOrgs.map((org) => (
              <MenuItem key={org.org_id} value={org.org_id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {userOrgs.length === 0 && (
        <Typography variant="overline" color="text.secondary">
          {contextLabel}
        </Typography>
      )}
      <FormControl size="small" sx={{ minWidth: 280 }}>
        <InputLabel id="workspace-switcher-label">Workspace</InputLabel>
        <Select
          labelId="workspace-switcher-label"
          label="Workspace"
          value={hasCurrentInContext ? currentWorkspaceId : ""}
          onChange={handleChange}
          disabled={loading || contextWorkspaces.length === 0}
        >
          {contextWorkspaces.map((w) => (
            <MenuItem key={w.workspace_id} value={w.workspace_id}>
              {w.workspace_name || "Untitled"}
            </MenuItem>
          ))}
          {contextWorkspaces.length === 0 && (
            <MenuItem value="" disabled>
              {loading ? "Loading…" : "No workspaces in this context"}
            </MenuItem>
          )}
        </Select>
      </FormControl>
      <Button
        variant="outlined"
        color="inherit"
        size="small"
        startIcon={<AddIcon />}
        onClick={openCreate}
      >
        New workspace
      </Button>
      {!hasCurrentInContext && currentWorkspaceId && !loading && (
        <Typography variant="caption" color="text.secondary">
          Current workspace is in a different context — pick one above to
          switch.
        </Typography>
      )}

      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>New workspace</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Workspace name"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !creating) handleCreate();
            }}
            error={!!createError}
            helperText={createError || `Created in ${contextLabel}.`}
            disabled={creating}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={creating}
            variant="outlined"
            color="cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="outlined"
            color="inherit"
            disabled={creating}
          >
            {creating ? <CircularProgress size={20} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkspaceSwitcher;
